import React from 'react';
import { Box, Typography, Card, CardContent, Link as MuiLink } from '@mui/material';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';

import BreadcrumbTrail from '../../../../layouts/BreadcrumbTrail';
import CenteredLoader from '../../../../components/loaders/PageLoader';

import { useEVNDetails } from '../../hooks/useEVNDetails';
import EVNDetailsTree from '../../components/EVNDetails/EVNDetailsTree';

import EVNDetailsSummaryCard from '../../components/EVNDetails/EVNDetailsSummaryCard';
import styles from './EVNDetails.styles';

import { useCrumb, useNavWithTrail } from '../../../../layouts/TrailProvider';

export default function EVNDetails() {
  const { isSidebarExpanded } = useOutletContext();
  const { evnId } = useParams();
  const navigate = useNavigate();
  const nav = useNavWithTrail(navigate);

  useCrumb(`EVN ${evnId}`, `/home/evns/${encodeURIComponent(evnId)}`);

  const { evnData, meters, loading } = useEVNDetails(evnId);

  const dash = (v) => (v == null || (typeof v === 'string' && v.trim() === '') ? '—' : v);

  const operatorValue =
    (evnData?.OperatorDescription && evnData.OperatorDescription.trim()) ||
    (evnData?.Operator && evnData.Operator.trim()) ||
    (evnData?.OperatorCode && evnData.OperatorCode.trim()) ||
    '—';

  const summaryFields = [
    ['Operator', operatorValue, false],
    [
      'Unit (Train)',
      evnData?.TrainNumber ?? null,
      false,
      evnData?.TrainNumber ? `/home/units/${encodeURIComponent(evnData.TrainNumber)}` : null,
    ],
    ['Created On', evnData?.CreatedOn ?? null, true],
    ['Meter Count', Array.isArray(meters) ? meters.length : '—', false],
  ];

  return (
    <Box
      sx={{
        ...styles.page,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      {loading ? (
        <CenteredLoader />
      ) : (
        <>
          <BreadcrumbTrail />

          <Box sx={styles.headerRow}>
            <Typography sx={styles.pageTitle}>
              {evnData?.EVN || evnId || 'EVN Details'}
            </Typography>
          </Box>

          <Box sx={styles.versionHistoryContainer}>
            <Typography variant="body2" sx={styles.versionInfoText}>
              Version history:{' '}
              <MuiLink
                onClick={() =>
                  nav(`/home/evns/${encodeURIComponent(evnId)}/history`, {
                    state: {
                      unit: evnData?.TrainNumber ?? null,
                      evn: evnData?.EVN ?? evnId ?? null,
                      trail: [
                        { label: 'EVNs', href: '/home/evns' },
                        { label: `EVN ${evnId}`, href: `/home/evns/${encodeURIComponent(evnId)}` },
                      ],
                    },
                  })
                }
                sx={styles.versionLink}
                underline="none"
                aria-label="View EVN version history"
                title="Click to view version history"
              >
                click here
              </MuiLink>
            </Typography>
          </Box>

          <Box sx={styles.grid}>
            <EVNDetailsSummaryCard summaryFields={summaryFields} dash={dash} evnId={evnId} />

            <Card sx={styles.detailCardSmall}>
              <Box sx={styles.cardHeaderRowLeft}>
                <Typography sx={styles.relatedMetersHeading}>Current Active Meters</Typography>
              </Box>

              <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {Array.isArray(meters) && meters.length > 0 ? (
                  <EVNDetailsTree
                    meters={meters}
                    unitName={evnData?.TrainNumber ?? null}
                    evnNumber={evnData?.EVN ?? evnId ?? null}
                  />
                ) : (
                  <Typography align="center">No active meters —</Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={styles.detailCardMedium}>
              <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Typography align="center" variant="body2" color="text.secondary">
                  Placeholder
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
}